import {
  type AddHighlightOptions,
  type HighlightLayerOptions,
  type HighlightsRenderer,
  type HighlightsRenderingStrategy
} from '@databases/highlights';
import { HIGHLIGHT_COLUMN_CLASS } from '@databases/highlights/constants';
import {
  DomHighlightsRenderer,
  MutableHighlightHandle
} from '@databases/highlights/internal';
import { type TableV2GeometryTarget } from '@databases/view-layout-table-v2/dom';

import styles from './highlight-fragment.module.css';
import {
  createRegionResolver,
  getHighlightRegions,
  projectRegion,
  type HighlightRegion,
  type RegionKey
} from './table-v2-highlight-regions.ts';

interface ActiveHighlight {
  options: AddHighlightOptions;
  readonly handle: MutableHighlightHandle;
  readonly elements: Map<RegionKey, HTMLElement>;
}

/**
 * V2 owns physical routing; feature layers retain logical IDs and bindings.
 * Host replacement rebinds only departing regions. Ordinary scroll redraws keep
 * the same elements, with no cell reads or selection-driven React updates.
 */
class TableV2HighlightsLayer implements HighlightsRenderer {
  private readonly active = new Map<string, ActiveHighlight>();
  private readonly regions = new Map<
    RegionKey,
    { host: HTMLElement; renderer: DomHighlightsRenderer }
  >();
  private hidden = false;
  private disposed = false;

  private readonly deps: {
    read: () => TableV2GeometryTarget | undefined;
    includeChrome: boolean;
    onDispose: () => void;
  };

  constructor(deps: TableV2HighlightsLayer['deps']) {
    this.deps = deps;
  }

  add(options: AddHighlightOptions) {
    if (this.disposed) {
      throw new Error('Cannot add a highlight to a disposed renderer.');
    }

    const active = this.active.get(options.range.id) ?? {
      options,
      handle: new MutableHighlightHandle(options.range.id),
      elements: new Map<RegionKey, HTMLElement>()
    };
    const refreshAppearance =
      active.options.className !== options.className ||
      active.options.zIndex !== options.zIndex;

    active.options = options;
    this.active.set(options.range.id, active);

    const context = this.syncRegions();

    this.syncHighlight(active, context, refreshAppearance);

    return active.handle;
  }

  update: HighlightsRenderer['update'] = (range, mode) => {
    const current = this.active.get(range.id);

    if (current) {
      this.add({ ...current.options, range, mode });
    }
  };

  private getRenderer(region: HighlightRegion) {
    let current = this.regions.get(region.key);

    if (!current) {
      const renderer = new DomHighlightsRenderer(
        region.host,
        createRegionResolver(() => {
          const target = this.deps.read();
          const latest =
            target && getHighlightRegions(target).find(({ key }) => key === region.key);

          return target && latest ? { target, region: latest } : undefined;
        }),
        () => {},
        false
      );

      current = { host: region.host, renderer };
      this.regions.set(region.key, current);

      if (this.hidden) {
        renderer.hideAll();
      }
    }

    return current.renderer;
  }

  private syncRegions() {
    const target = this.deps.read();
    const regions = target?.available ? getHighlightRegions(target) : [];

    for (const [key, current] of this.regions) {
      if (regions.find((region) => region.key === key)?.host !== current.host) {
        for (const { handle, elements } of this.active.values()) {
          handle.removeFragment(key);
          elements.delete(key);
        }

        current.renderer.dispose();
        this.regions.delete(key);
      }
    }

    return { target, regions };
  }

  redrawAll() {
    if (this.disposed) {
      return;
    }

    const context = this.syncRegions();

    for (const active of this.active.values()) {
      this.syncHighlight(active, context, false);
    }
  }

  private syncHighlight(
    { options, handle, elements }: ActiveHighlight,
    {
      target,
      regions
    }: { target: TableV2GeometryTarget | undefined; regions: HighlightRegion[] },
    refreshAppearance: boolean
  ) {
    const fragments: Parameters<MutableHighlightHandle['setFragments']>[0][number][] = [];

    for (const region of regions) {
      const projection =
        target &&
        projectRegion({
          region,
          options,
          target,
          includeChrome: this.deps.includeChrome
        });

      if (!projection) {
        handle.removeFragment(region.key);
        elements.delete(region.key);
        this.regions.get(region.key)?.renderer.remove(options.range.id);

        continue;
      }

      const role = fragments.length === 0 ? 'primary' : 'continuation';
      const renderer = this.getRenderer(region);

      let element = elements.get(region.key);

      if (!element || refreshAppearance) {
        if (element) {
          /**
           * Clean up before addElement resets classes; the retained DOM node
           * must bind again when setFragments publishes the refreshed appearance.
           */
          handle.removeFragment(region.key);
        }

        element = renderer.addElement(projection.options);

        // V1 opens column tops with a class; V2 owns all joins through projection.
        element.classList.remove(HIGHLIGHT_COLUMN_CLASS);
        element.classList.add(styles.fragment);
        elements.set(region.key, element);
      } else {
        // Geometry redraws must preserve classes attached by handle bindings.
        renderer.update(projection.options.range, projection.options.mode);
      }

      element.id = role === 'primary' ? (options.elementId ?? '') : '';
      element.dataset.tableV2HighlightRegion = region.key;

      const { top, right, bottom, left } = projection.open;

      element.toggleAttribute('data-open-top', top);
      element.toggleAttribute('data-open-right', right);
      element.toggleAttribute('data-open-bottom', bottom);
      element.toggleAttribute('data-open-left', left);

      fragments.push({ key: region.key, role, element });
    }

    handle.setFragments(fragments);
  }

  hideAll() {
    this.hidden = true;

    for (const { renderer } of this.regions.values()) {
      renderer.hideAll();
    }
  }

  showAll() {
    this.hidden = false;

    for (const { renderer } of this.regions.values()) {
      renderer.showAll();
    }

    this.redrawAll();
  }

  remove(id: string) {
    this.active.get(id)?.handle.dispose();
    this.active.delete(id);

    for (const { renderer } of this.regions.values()) {
      renderer.remove(id);
    }

    if (!this.active.size) {
      this.releaseRegions();
    }
  }

  private releaseRegions() {
    for (const { renderer } of this.regions.values()) {
      renderer.dispose();
    }

    this.regions.clear();
  }

  clear() {
    for (const { handle } of this.active.values()) {
      handle.dispose();
    }

    this.active.clear();
    this.releaseRegions();
  }

  dispose() {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.clear();
    this.deps.onDispose();
  }
}

/**
 * Bootstrap installs this once per V2 controller. Renderer registration owns
 * availability; no DOM observer, service lookup or extra scroll listener is needed.
 */
export function createTableV2HighlightsStrategy(
  read: () => TableV2GeometryTarget | undefined
): HighlightsRenderingStrategy {
  const layers = new Set<TableV2HighlightsLayer>();

  return {
    createLayer(options?: HighlightLayerOptions) {
      const layer = new TableV2HighlightsLayer({
        read,
        includeChrome: options?.coverage === 'body-and-chrome',
        onDispose: () => layers.delete(layer)
      });

      layers.add(layer);

      return layer;
    },

    dispose() {
      for (const layer of [...layers]) {
        layer.dispose();
      }
    }
  };
}
